import { useEffect, useEffectEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { addGlobalContexts } from '@snowplow/browser-tracker';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { getEnv, useVehicleLookup } from '@motorway/motorway-handshake-components';
import type { Enquiry } from '@motorway/motorway-handshake-components/types';

import { useDriver } from '../contexts/Driver';
import { ROUTES } from '../helpers/routes';
import { AUTH_CODE_QUERY_PARAM, getSessionToken, getVehicleQueryParams, setSessionToken } from './helpers/auth.helpers';

import type { RedeemResponse } from '../@types/Redeem';

export const useAuth = () => {
  const { driver, setDriver } = useDriver();
  const { getEnquiry, vehicle } = useVehicleLookup();

  const navigate = useNavigate();

  const signInDriver = (data: RedeemResponse) => {
    setDriver({ ...data });
    setSessionToken({ token: data.token, enquiryId: data.enquiryId });
    navigate(ROUTES.INSPECTION, { replace: true });
  };

  useEffect(() => {
    const { enquiryId, token } = driver ?? {};
    if (!token || enquiryId === vehicle?.enquiry?.id) {
      return;
    }

    getEnquiry();
  }, [driver?.token, vehicle?.enquiry?.id]);

  useEffect(() => {
    if (!vehicle?.enquiry?.id) {
      return;
    }

    const enquiryId = vehicle.enquiry.id;

    const { closingBid, state } = vehicle.enquiry;
    const { dealerContact } = closingBid;
    const { slug } = state;

    addGlobalContexts([
      {
        schema: 'iglu:uk.co.motorway/user_dealer_contact/jsonschema/2-0-0',
        data: {
          dealer_site_name: dealerContact.name,
          logged_in: true,
          user_dealer_contact_id: dealerContact.id,
          user_dealer_site_id: dealerContact.dealerId,
        },
      },
      {
        schema: 'iglu:uk.co.motorway/enquiry/jsonschema/1-0-0',
        data: {
          dp_enquiry_id: enquiryId,
          dp_enquiry_status: slug,
          plt_enquiry_id: -1,
          plt_enquiry_status: 'N/A',
        },
      },
    ]);

    const { navigator } = globalThis;

    const DD_CONTEXT = {
      connection: navigator.connection?.effectiveType || 'unknown',
      email: dealerContact.email,
      enquiryId,
      id: String(dealerContact.id),
      name: dealerContact.name,
    };

    const globalContext = datadogLogs.getGlobalContext() ?? {};

    datadogRum.setUser(DD_CONTEXT);
    datadogLogs.setGlobalContext({
      ...globalContext,
      usr: { ...DD_CONTEXT },
      env: getEnv(),
    });
  }, [vehicle]);

  return { signInDriver };
};

export const Auth = () => {
  useAuth();

  const [checking, setChecking] = useState(true);

  const { driver, setDriver } = useDriver();
  const { setVehicle } = useVehicleLookup();

  const { code, enquiryId, token } = driver ?? {};

  const navigate = useNavigate();
  const navigateEvent = useEffectEvent(navigate);

  useEffect(() => {
    const { token: sessionToken, enquiryId: sessionEnquiryId } = getSessionToken();
    const url = new URL(window.location.href);
    const queryCode = url.searchParams.get(AUTH_CODE_QUERY_PARAM);

    let driverData = {};

    switch (true) {
      case !!queryCode:
        driverData = { code: queryCode };
        break;
      case !!sessionToken:
        driverData = { token: sessionToken, enquiryId: sessionEnquiryId };
        break;
    }

    setDriver(driverData);
    setChecking(false);

    const vehicleParams = getVehicleQueryParams();

    if (vehicleParams) {
      const [vrm, makeName, model] = vehicleParams;

      setVehicle({
        enquiry: {
          closingBid: {},
          vehicleDetails: {
            prettyVrm: vrm,
          },
          vehicle: {
            make: {
              name: makeName,
            },
            model,
          },
        },
      } as Enquiry);
    }
  }, [setDriver]);

  useEffect(() => {
    // Allow the time for the context to populate on the rerender in the useEffect above
    if (checking) {
      return;
    }

    if (code && !token) {
      navigateEvent(`${ROUTES.OTP_CODE}${window.location.search}`);
      return;
    }

    if (token && enquiryId) {
      setDriver({ token, enquiryId });
      return;
    }

    navigateEvent(ROUTES.HOME);
  }, [checking, code, enquiryId, setDriver, token]);

  return null;
};
